// © Microsoft Corporation. All rights reserved.

import { AccessibilityHelpers, BridgeEventNames, ResponsiveClassManager, SocialTelemetryManager, addOrUpdateTelemetryObject, appErrorLogger, enums, handleBridgeEvent, hasThemeBase, preventSelection, renderHybrid } from "@msnews/social-utils";
import { AppError, logger } from "@msnews/diagnostics";
import { BaseExperience, InitialCommonExperienceProps } from "@msnews/experiences-base";
import { HandledPropsKeys, getContextData } from "@msnews/core";
import { SocialBarHandledProps, SocialBarUnhandledProps } from "./SocialBar.props";

import ContentSocialBar from "./content-social-bar/ContentSocialBar";
import { ExperienceIds } from "@msnews/experiences-constants";
import React from "react";
import { RenderErrors } from "./SocialBar.errors";
import { SocialService } from "@msnews/social-data-service";
import { canUseDOM } from "@msnews/isomorphic-util";
import { classNames } from "@microsoft/fast-web-utilities";

declare global {
    interface Window {
        loginWindow: any;
        trackFromWidget: any;
        sapphireBridge: any;
        msaLoginWindow: any;
        signalR: any;
        auth2: any;
        googleInit: any;
        gapi: any;
        FB: any;
        fbAsyncInit: any;
        listenMSALoginWindow: any;
        msaLogin: any;
        clipboardData: any;
        AzureStorage: any;
        require: (r: any, f: any) => void;
        ReactNativeWebView: any;
    }
}

/**
 * Starter kit experience
 */
export class SocialBar extends BaseExperience<SocialBarHandledProps, SocialBarUnhandledProps, {}> {

    /** The SocialBar default prop contract. Describes any default values for any component handled props */
    public static defaultProps: Partial<SocialBarHandledProps> = {
        experienceName: ExperienceIds.socialBar
    };
    private handleTelemetryCall: Function;
    private handlePartialPageImpressionCall: Function;
    private handleUpdateTemplValueCallback: Promise<Function>;
    private handleAppErrorEventCall: (AppError) => void;
    telemetryObjects: any;

    constructor(props, context) {
        super(props, context);

        this.handleTelemetryCall = this.props.handleTelemetryCall;
        this.handlePartialPageImpressionCall = this.props.handlePartialPageImpressionCall;
        this.handleAppErrorEventCall = this.props.handleAppErrorEventCall;
        this.handleUpdateTemplValueCallback = this.props.handleUpdateTemplValueCallback;
        if (this.handleAppErrorEventCall) {
            appErrorLogger.setOnAppError(this.handleAppErrorEventCall);
        }
        this.telemetryObjects = {};
    }

    /** @inheritdoc */
    protected handledProps: HandledPropsKeys<SocialBarHandledProps> = {
        ...InitialCommonExperienceProps,
        checkAndLogReducerError: undefined
    };

    /** @inheritdoc */
    public componentWillMount(): void {
        try {
            const locationHref = getContextData(this.props.requestContext.data.locationHref, "locationHref");
            const ppeEnvironmentSelected = locationHref.toLowerCase().indexOf(enums.selectPpeParameter) > -1;
            if (ppeEnvironmentSelected) { SocialService.setDomain(enums.externalUrls.ppeApi); }
        } catch (e) {
            logger.log("Error occured using requestContext.");
        }
    }

    /** @ineritdoc */
    public componentDidMount(): void {
        this.setupTelemetry();
        this.setupBridge();
        this.setupResponsiveClass();
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        isSuperApp && this.preventSelectionInSuperapp();
    }

    /** @inheritdoc */
    public componentWillUnmount(): void {
        this.teardownBridge();
    }

    public componentDidCatch(error, info): void {
        const componentRegex = new RegExp("in (.+) .created by");
        const componentNameResult = info.componentStack.match(componentRegex);
        let componentName = componentNameResult && componentNameResult[1] || "unknown";
        const reduxResult = componentName.match(/Connect\((.+)\)/);
        const isRedux = (reduxResult && reduxResult[1]) != null;
        if (isRedux) {
            componentName = reduxResult[1];
        }
        const isPopup = info.componentStack.indexOf("created by Popup") > 0;
        const appError = RenderErrors[componentName] || RenderErrors.UnknownComponent;

        const { id } = this.props;

        appErrorLogger.log({
            ...appError,
            message: "SocialBar experience component failed to render",
            pb: {
                ...appError.pb,
                customMessage: componentName + " " + error.name + ": " + error.message,
                stack: error.stack,
                socialProps: { id },
                isPopup,
                isRedux
            }
        } as AppError);
    }

    public componentDidUpdate() {
        this.props.checkAndLogReducerError();
        AccessibilityHelpers.attachAccessibilityListeners(document);
    }

    public preventSelectionInSuperapp() {
        document.addEventListener("touchstart", preventSelection);
    }

    public preparePropsToPass(propsToPass) {
        // Add and pass a container telemetry object matching with the module
        const moduleTelemetryContract = { name: "Socialbar" };
        this.telemetryObjects.module = addOrUpdateTelemetryObject(this.props.mainTelemetryObject, moduleTelemetryContract, {}, this.telemetryObjects.module);
        propsToPass.mainTelemetryObject = this.telemetryObjects.module;
    }

    /** @inheritdoc */
    public render(): JSX.Element {
        super.render();

        const { t, theme, children, requestContext, managedClasses } = this.props;
        const propsToPass = {...this.unhandledProps(), children, markTtvr: this.markTtvr, t, theme, requestContext};
        this.preparePropsToPass(propsToPass);

        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        const { mainContainer, mainSuperAppContainer, mainInSuperappModal } = managedClasses;
        return (
            <div
                className={classNames(
                    mainContainer,
                    [mainSuperAppContainer, isSuperApp],
                    ["no-select", isSuperApp],
                    [mainInSuperappModal, false]
                )}
                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.module, false)}
            >
                <ContentSocialBar {...propsToPass} />
            </div>
        );
    }

    private setupTelemetry = () => {
        if (this.handleTelemetryCall && this.handlePartialPageImpressionCall) {
            SocialTelemetryManager.setPrimeHandlers({
                handleTelemetryCall: this.handleTelemetryCall,
                handlePartialPageImpressionCall: this.handlePartialPageImpressionCall,
                handleUpdateTemplValueCallback: this.handleUpdateTemplValueCallback
            });
        }
    };

    private setupBridge = () => {
        if (canUseDOM()) {
            Object.keys(BridgeEventNames).forEach(eventName => document.addEventListener(BridgeEventNames[eventName], handleBridgeEvent));
        }
    };

    private teardownBridge = () => {
        if (canUseDOM()) {
            Object.keys(BridgeEventNames).forEach(eventName => document.removeEventListener(BridgeEventNames[eventName], handleBridgeEvent));
        }
    };

    /** Helper to mark time to visually ready */
    private markTtvr = () => {
        window.requestAnimationFrame(() => {
            setTimeout(() => {
                super.markTimeToVisuallyReady(true);
            }, 0);
        });
    };

    private setupResponsiveClass() {
        ResponsiveClassManager.setWindow(window);
        ResponsiveClassManager.addListener(this, (responsiveClass) => {
            if (this.props.styleConnector && responsiveClass != this.props.responsiveClass) {
                this.props.styleConnector.updateResponsiveClass(responsiveClass);
            }
        });
    }
}